import { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import {
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
  Tooltip,
} from '@mui/material'
import { Add, Delete } from '@mui/icons-material'
import DialogBox from '../components/DialogBox'
import FormErrorsModal from '../../../components/FormErrorsModal'
import SuccessModal from '../../../components/SuccessModal'
import PlatformIcon from '../components/PlatformIcon'
import axiosInstance from '../../../utils/axiosInstance'
import { leadPlatforms, leadStatuses } from '../../../constants'

interface CreateLeadForOrderProps {
  open: boolean
  setOpen: (open: boolean) => void
  orderId: string
  onSuccess?: () => void
}

interface MetaField {
  name: string
  values: string[]
}

interface FormFields {
  firstName: string
  lastName: string
  email: string
  phone: string
}

interface CustomField {
  id: string
  question: string
  answer: string
}

function CreateLeadForOrder({ open, setOpen, orderId, onSuccess }: CreateLeadForOrderProps) {
  const intl = useIntl()
  const [formFields, setFormFields] = useState<FormFields>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  })

  const [customFields, setCustomFields] = useState<CustomField[]>([])
  const [, setExistingFieldStructure] = useState<MetaField[]>([])

  const [formikErrors, setFormikErrors] = useState<string[]>([])
  const [errorsModalOpen, setErrorsModalOpen] = useState(false)
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [status, setStatus] = useState('new')
  const [platform, setPlatform] = useState('tiktok')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Fetch existing leads and prefill structure from first lead
  useEffect(() => {
    const fetchExistingLeads = async () => {
      if (!open || !orderId) return

      try {
        const response = await axiosInstance.get(`/leads-by-order/${orderId}`)
        const leads = response.data.leads || []

        // Helper function to check if a lead has custom fields
        const hasCustomFields = (fieldData: any): boolean => {
          if (!fieldData) return false

          const parsedData = typeof fieldData === 'string' ? JSON.parse(fieldData) : fieldData

          if (!Array.isArray(parsedData)) return false

          return parsedData.some((field: MetaField) => {
            const fieldName = field.name?.toLowerCase() || ''
            return (
              fieldName !== 'full_name' && fieldName !== 'email' && fieldName !== 'phone_number'
            )
          })
        }

        // Find the first lead with custom fields
        const leadWithCustomFields = leads.find(
          (lead: any) => lead.platform && hasCustomFields(lead.fieldData)
        )

        if (leadWithCustomFields && leadWithCustomFields.fieldData) {
          // Parse fieldData
          const parsedFieldData =
            typeof leadWithCustomFields.fieldData === 'string'
              ? JSON.parse(leadWithCustomFields.fieldData)
              : leadWithCustomFields.fieldData

          // Store the structure for consistency
          setExistingFieldStructure(parsedFieldData)

          // Prefill custom fields from existing lead structure
          if (Array.isArray(parsedFieldData)) {
            const customFieldsFromExisting: CustomField[] = []

            parsedFieldData.forEach((field: MetaField) => {
              const fieldName = field.name?.toLowerCase() || ''

              // Skip standard fields (full_name, email, phone_number)
              if (
                fieldName === 'full_name' ||
                fieldName === 'email' ||
                fieldName === 'phone_number'
              ) {
                return
              }

              // Add as custom field with empty answer
              customFieldsFromExisting.push({
                id: `prefilled-${field.name}`,
                question: field.name,
                answer: '',
              })
            })

            // Set the prefilled custom fields
            setCustomFields(customFieldsFromExisting)
          }
        }
      } catch (error) {
        console.error('Error fetching existing leads:', error)
      }
    }

    fetchExistingLeads()
  }, [open, orderId])

  const handleFieldChange = (field: keyof FormFields, value: string) => {
    setFormFields((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const addCustomField = () => {
    setCustomFields((prev) => [
      ...prev,
      {
        id: `custom-${Date.now()}`,
        question: '',
        answer: '',
      },
    ])
  }

  const removeCustomField = (id: string) => {
    setCustomFields((prev) => prev.filter((field) => field.id !== id))
  }

  const updateCustomField = (id: string, field: 'question' | 'answer', value: string) => {
    setCustomFields((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    )
  }

  // Convert technical field names to human-friendly display text
  const formatFieldNameForDisplay = (fieldName: string): string => {
    // Replace underscores with spaces
    const withSpaces = fieldName.replace(/_/g, ' ')
    // Capitalize first letter of each word
    return withSpaces
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const convertToMetaFieldData = (): MetaField[] => {
    const fieldData: MetaField[] = []

    // Combine first name and last name into full_name
    const fullName = `${formFields.firstName} ${formFields.lastName}`.trim()
    if (fullName) {
      fieldData.push({
        name: 'full_name',
        values: [fullName],
      })
    }

    // Add email
    if (formFields.email) {
      fieldData.push({
        name: 'email',
        values: [formFields.email],
      })
    }

    // Add phone
    if (formFields.phone) {
      fieldData.push({
        name: 'phone_number',
        values: [formFields.phone],
      })
    }

    // Add custom fields - preserve field names from existing structure
    customFields.forEach((field) => {
      if (field.question && field.answer) {
        // Use the exact field name if it came from existing structure, otherwise normalize it
        const fieldName = field.id.startsWith('prefilled-')
          ? field.question // Keep original name from existing lead
          : field.question.toLowerCase().replace(/\s+/g, '_') // Normalize new fields

        fieldData.push({
          name: fieldName,
          values: [field.answer],
        })
      }
    })

    return fieldData
  }

  const handleSubmit = async () => {
    // Basic validation
    if (
      !formFields.firstName &&
      !formFields.lastName &&
      !formFields.email &&
      !formFields.phone &&
      customFields.length === 0
    ) {
      setFormikErrors(['Please fill at least one field'])
      setErrorsModalOpen(true)
      return
    }

    setIsSubmitting(true)

    try {
      const fieldData = convertToMetaFieldData()

      const response = await axiosInstance.post('/lead/create-for-order', {
        orderId,
        fieldData,
        status,
        platform,
      })

      if (response.status === 201 || response.status === 200) {
        setSuccessModalOpen(true)
        // Reset form
        setFormFields({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
        })
        setCustomFields([])
        setStatus('new')
        setPlatform('tiktok')
      }
    } catch (error: any) {
      setErrorsModalOpen(true)
      setFormikErrors([error?.response?.data?.message || error.message || 'Failed to create lead'])
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSuccessClose = () => {
    setSuccessModalOpen(false)
    setOpen(false)
    if (onSuccess) {
      onSuccess()
    }
  }

  return (
    <>
      <DialogBox
        open={open}
        title="Create Lead"
        handleSubmit={handleSubmit}
        setOpen={(value) => setOpen(typeof value === 'function' ? value(open) : value)}
        isLoading={isSubmitting}
      >
        <Container>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  id="status"
                  name="status"
                  onChange={(e) => setStatus(e.target.value)}
                  value={status}
                  label={intl.formatMessage({ id: 'status' })}
                >
                  {leadStatuses.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="platform-label">Platform</InputLabel>
                <Select
                  labelId="platform-label"
                  id="platform"
                  name="platform"
                  onChange={(e) => setPlatform(e.target.value)}
                  value={platform}
                  label={intl.formatMessage({ id: 'platform' })}
                >
                  {leadPlatforms.map((platformOption) => (
                    <MenuItem key={platformOption.value} value={platformOption.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PlatformIcon platform={platformOption.value as any} />
                        <Typography>{platformOption.label}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="First Name"
                name="firstName"
                variant="outlined"
                fullWidth
                value={formFields.firstName}
                onChange={(e) => handleFieldChange('firstName', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Last Name"
                name="lastName"
                variant="outlined"
                fullWidth
                value={formFields.lastName}
                onChange={(e) => handleFieldChange('lastName', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                name="email"
                type="email"
                variant="outlined"
                fullWidth
                value={formFields.email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Phone"
                name="phone"
                variant="outlined"
                fullWidth
                value={formFields.phone}
                onChange={(e) => handleFieldChange('phone', e.target.value)}
              />
            </Grid>

            {/* Custom fields section */}
            <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1">Additional Fields</Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={addCustomField}
                    size="small"
                  >
                    Add Field
                  </Button>
                </Box>

                {customFields.map((field) => {
                  const isPrefilled = field.id.startsWith('prefilled-')
                  const displayQuestion = isPrefilled
                    ? formatFieldNameForDisplay(field.question)
                    : field.question

                  return (
                    <Box
                      key={field.id}
                      sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'start' }}
                    >
                      <Tooltip title={displayQuestion} arrow placement="top">
                        <TextField
                          label="Question"
                          variant="outlined"
                          fullWidth
                          value={displayQuestion}
                          onChange={(e) => updateCustomField(field.id, 'question', e.target.value)}
                          placeholder="e.g., Wann bist du am besten erreichbar?"
                          disabled={isPrefilled}
                          InputProps={{
                            readOnly: isPrefilled,
                          }}
                        />
                      </Tooltip>
                      <TextField
                        label="Answer"
                        variant="outlined"
                        fullWidth
                        value={field.answer}
                        onChange={(e) => updateCustomField(field.id, 'answer', e.target.value)}
                        placeholder="e.g., abends"
                      />
                      <IconButton
                        onClick={() => removeCustomField(field.id)}
                        color="error"
                        sx={{ mt: 1 }}
                        disabled={isPrefilled}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  )
                })}
              </Box>
            </Grid>
          </Grid>
        </Container>

        <FormErrorsModal
          errorsModalOpen={errorsModalOpen}
          formikErrors={formikErrors}
          setErrorsModalOpen={setErrorsModalOpen}
        />
        <SuccessModal
          successModalOpen={successModalOpen}
          handleClose={handleSuccessClose}
          setSuccessModalOpen={setSuccessModalOpen}
          message="Lead created successfully"
        />
      </DialogBox>
    </>
  )
}

export default CreateLeadForOrder
